import React, { useEffect, useState, useRef, useMemo } from "react";
import { api } from "../lib/api";
import { useSiteContext } from "../components/layout/Layout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../components/ui/sheet";
import { Plus, Trash2, ImagePlus, Upload, Edit3, Eye, Filter, Download, Move, Pencil, Map as MapIcon, FileWarning, X } from "lucide-react";
import { toast } from "sonner";
import { toPng } from "html-to-image";
import { EQUIPMENT, EQUIPMENT_GROUPS, equipmentMeta, pinStatusFromTelemetry, STATUS_BG, STATUS_RING } from "../components/acms/Equipment";
import { Badge } from "../components/ui/badge";
import { usePermissions } from "../hooks/usePermissions";

// Floor plans are images only (PNG / JPEG / WebP) — pins are placed on the image itself, which a
// PDF <embed> can't support. The backend enforces the same rule (tenant.py _check_floor_image).
// Floors uploaded as PDF before this change still render, with a prompt to replace them.
const ACCEPTED_TYPES = ["image/png", "image/jpeg", "image/webp"];
const MAX_FILE_BYTES = 15 * 1024 * 1024;
const MAX_IMAGE_WIDTH = 1800;

const EMPTY_FORM = { floor_number: 1, name: "", image_url: "", file_name: "" };

function floorLabel(f) {
  return f.floor_number === 0 ? "Basement" : `Floor ${f.floor_number}`;
}

function isPdfUrl(url) {
  return typeof url === "string" && url.startsWith("data:application/pdf");
}

// Downscale to ≤1800px wide and re-encode as JPEG. Painted onto white first — JPEG has no alpha,
// so a transparent PNG blueprint would otherwise come out with a black background.
function compressImage(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const scale = Math.min(1, MAX_IMAGE_WIDTH / img.width);
        const canvas = document.createElement("canvas");
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        const ctx = canvas.getContext("2d");
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL("image/jpeg", 0.85));
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function ImageDropzone({ value, fileName, onFile, onClear, testid }) {
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef(null);

  if (value && !isPdfUrl(value)) {
    return (
      <div className="relative rounded-lg overflow-hidden border border-gray-200 bg-slate-50">
        <img src={value} alt="Floor plan preview" className="w-full max-h-64 object-contain" />
        <div className="flex items-center justify-between gap-2 px-3 py-2 border-t border-gray-200 bg-white text-xs">
          <span className="truncate text-gray-600">{fileName || "Current image"}</span>
          <div className="flex items-center gap-1 shrink-0">
            <Button type="button" size="sm" variant="outline" className="h-7" onClick={() => inputRef.current?.click()}>
              <Upload className="w-3.5 h-3.5 mr-1" strokeWidth={2.5} /> Replace
            </Button>
            <Button type="button" size="sm" variant="ghost" className="h-7 text-gray-500" onClick={onClear} aria-label="Remove image">
              <X className="w-3.5 h-3.5" strokeWidth={2.5} />
            </Button>
          </div>
        </div>
        <input ref={inputRef} type="file" accept={ACCEPTED_TYPES.join(",")} className="hidden" onChange={(e) => { onFile(e.target.files?.[0]); e.target.value = ""; }} />
      </div>
    );
  }

  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={(e) => { e.preventDefault(); setDragging(false); onFile(e.dataTransfer.files?.[0]); }}
      onClick={() => inputRef.current?.click()}
      className={`rounded-lg border-2 border-dashed p-8 text-center cursor-pointer transition ${dragging ? "border-orange-500 bg-orange-50" : "border-gray-300 bg-gray-50 hover:border-orange-400 hover:bg-orange-50/50"}`}
      data-testid={testid}
    >
      <ImagePlus className="w-9 h-9 mx-auto mb-2 text-orange-500" strokeWidth={2} />
      <div className="text-sm font-semibold text-gray-800">Drop a floor plan image or click to browse</div>
      <div className="text-xs text-gray-500 mt-1">PNG, JPEG or WebP · up to 15 MB · resized to {MAX_IMAGE_WIDTH}px wide</div>
      <input ref={inputRef} type="file" accept={ACCEPTED_TYPES.join(",")} className="hidden" onChange={(e) => { onFile(e.target.files?.[0]); e.target.value = ""; }} data-testid="floor-file-input" />
    </div>
  );
}

export default function FloorPlans() {
  const { sites, selectedSite } = useSiteContext();
  // Adding/editing floors and pins is super-admin-only (backend enforces the same); everyone else
  // gets the live, read-only plan.
  const { can } = usePermissions();
  const canManage = can("floor_plans", "update");
  const [floors, setFloors] = useState([]);
  const [activeFloor, setActiveFloor] = useState(null);
  const [tele, setTele] = useState(null);
  const [devices, setDevices] = useState([]);
  // Add / edit floor dialog — `editing` holds the floor being edited, null when adding.
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [pinDevice, setPinDevice] = useState("");
  const [pinDeviceType, setPinDeviceType] = useState("smoke_detector");
  const [showPinPicker, setShowPinPicker] = useState(false);
  const [pendingPin, setPendingPin] = useState(null);
  const [activePin, setActivePin] = useState(null);
  const [hiddenGroups, setHiddenGroups] = useState(new Set());
  const [draggingIdx, setDraggingIdx] = useState(null);
  const [exporting, setExporting] = useState(false);
  const imgRef = useRef(null);
  const canvasRef = useRef(null);
  // Rendered width/height ratio of the current plan, read once it loads — used to cap the canvas
  // width so the whole plan fits the viewport height.
  const [imgRatio, setImgRatio] = useState(null);

  const load = async () => {
    if (!selectedSite) return;
    const r = await api.get("/floors", { params: { site_id: selectedSite } });
    const sorted = r.data.slice().sort((a, b) => (a.floor_number ?? 0) - (b.floor_number ?? 0));
    setFloors(sorted);
    setActiveFloor((cur) => {
      if (!sorted.length) return null;
      return sorted.find((f) => f.floor_id === cur?.floor_id) || sorted[0];
    });
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [selectedSite]);

  useEffect(() => {
    if (!selectedSite) return;
    const fetch = () => {
      api.get(`/telemetry/${selectedSite}`).then((r) => setTele(r.data)).catch(() => {});
      api.get("/devices", { params: { site_id: selectedSite } }).then((r) => setDevices(r.data)).catch(() => {});
    };
    fetch();
    const i = setInterval(fetch, 5000);
    return () => clearInterval(i);
  }, [selectedSite]);

  useEffect(() => { setEditMode(false); setActivePin(null); }, [activeFloor?.floor_id]);

  useEffect(() => { setImgRatio(null); }, [activeFloor?.image_url]);

  // The image always renders at the wrapper's full width (w-full h-auto), which every format
  // sizes correctly — including the seeded SVG plans, which carry only a viewBox and no intrinsic
  // width/height (a shrink-to-fit wrapper collapsed those to 0×0). The ratio is read from the
  // rendered box rather than naturalWidth/Height, which viewBox-only SVGs don't report reliably.
  const onImgLoad = () => {
    const rect = imgRef.current?.getBoundingClientRect();
    if (rect && rect.width > 0 && rect.height > 0) setImgRatio(rect.width / rect.height);
  };

  const handleFile = async (file) => {
    if (!file) return;
    if (!ACCEPTED_TYPES.includes(file.type)) {
      toast.error("Only PNG, JPEG or WebP images are supported");
      return;
    }
    if (file.size > MAX_FILE_BYTES) { toast.error("Image too large (max 15 MB)"); return; }
    try {
      const dataUrl = await compressImage(file);
      setForm((f) => ({ ...f, image_url: dataUrl, file_name: file.name }));
    } catch { toast.error("Could not read that image"); }
  };

  const openAdd = () => {
    const nextNumber = floors.length ? Math.max(...floors.map((f) => f.floor_number ?? 0)) + 1 : 1;
    setEditing(null);
    setForm({ ...EMPTY_FORM, floor_number: nextNumber });
    setDialogOpen(true);
  };

  const openEdit = () => {
    if (!activeFloor) return;
    setEditing(activeFloor);
    setForm({
      floor_number: activeFloor.floor_number ?? 1,
      name: activeFloor.name || "",
      // A legacy PDF can't be kept as-is — the image field starts empty so a new image is required.
      image_url: isPdfUrl(activeFloor.image_url) ? "" : activeFloor.image_url,
      file_name: "",
    });
    setDialogOpen(true);
  };

  const saveFloor = async () => {
    if (!form.name.trim()) return toast.error("Give the floor a name");
    if (!form.image_url) return toast.error("Upload a floor plan image");
    setSaving(true);
    try {
      if (editing) {
        const patch = { name: form.name.trim(), floor_number: form.floor_number };
        if (form.image_url !== editing.image_url) patch.image_url = form.image_url;
        await api.patch(`/floors/${editing.floor_id}`, patch);
        toast.success("Floor plan updated");
      } else {
        await api.post("/floors", { floor_number: form.floor_number, name: form.name.trim(), image_url: form.image_url, site_id: selectedSite });
        toast.success("Floor plan added");
      }
      setDialogOpen(false);
      setForm(EMPTY_FORM);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  // Pin coordinates are stored as % of the image. The canvas wrapper is sized to the image itself
  // (no object-contain letterboxing), so the image's bounding box is exactly the placement area.
  const pctFromEvent = (e) => {
    const rect = imgRef.current.getBoundingClientRect();
    return {
      x: Math.max(0, Math.min(100, ((e.clientX - rect.left) / rect.width) * 100)),
      y: Math.max(0, Math.min(100, ((e.clientY - rect.top) / rect.height) * 100)),
    };
  };

  const handleImgClick = (e) => {
    if (!activeFloor || !editMode || draggingIdx !== null || !imgRef.current) return;
    setPendingPin(pctFromEvent(e));
    setPinDevice("");
    setShowPinPicker(true);
  };

  // Drag-to-reposition pin handlers
  const onPinMouseDown = (e, idx) => {
    if (!editMode) return;
    e.stopPropagation();
    e.preventDefault();
    setDraggingIdx(idx);
  };
  const onCanvasMouseMove = (e) => {
    if (draggingIdx === null || !imgRef.current) return;
    const { x, y } = pctFromEvent(e);
    setActiveFloor((f) => {
      if (!f) return f;
      const pins = f.pins.map((p, i) => (i === draggingIdx ? { ...p, x, y } : p));
      return { ...f, pins };
    });
  };
  const onCanvasMouseUp = async () => {
    if (draggingIdx === null) return;
    try {
      await api.patch(`/floors/${activeFloor.floor_id}`, { pins: activeFloor.pins });
      toast.success("Pin moved");
    } catch {
      toast.error("Save failed");
      load();
    } finally {
      setDraggingIdx(null);
    }
  };

  const exportPng = async () => {
    if (!canvasRef.current) return;
    setExporting(true);
    try {
      const dataUrl = await toPng(canvasRef.current, {
        cacheBust: true,
        pixelRatio: 2,
        filter: (node) => !(node?.classList?.contains?.("no-export")),
      });
      const a = document.createElement("a");
      a.href = dataUrl;
      a.download = `${activeFloor.name.replace(/\s+/g, "_")}_pins.png`;
      a.click();
      toast.success("PNG exported");
    } catch (e) {
      toast.error("Export failed: " + e.message?.slice(0, 60));
    } finally {
      setExporting(false);
    }
  };

  const savePin = async () => {
    if (!pinDevice) return toast.error("Select a device");
    const dev = devices.find((d) => d.device_id === pinDevice);
    const newPin = {
      ...pendingPin,
      device_id: pinDevice,
      device_name: dev?.device_name,
      device_type: dev?.device_type || pinDeviceType,
    };
    const updated = [...(activeFloor.pins || []), newPin];
    try {
      await api.patch(`/floors/${activeFloor.floor_id}`, { pins: updated });
      toast.success("Pin placed");
      setShowPinPicker(false); setPendingPin(null); setPinDevice("");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not place pin"); }
  };

  const deletePin = async (idx) => {
    if (!window.confirm("Delete this pin?")) return;
    const updated = activeFloor.pins.filter((_, i) => i !== idx);
    try {
      await api.patch(`/floors/${activeFloor.floor_id}`, { pins: updated });
      toast.success("Pin removed"); setActivePin(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not remove pin"); }
  };

  const deleteFloor = async () => {
    if (!activeFloor) return;
    if (!window.confirm(`Delete "${activeFloor.name}" floor plan and its ${activeFloor.pins?.length || 0} pins?`)) return;
    try {
      await api.delete(`/floors/${activeFloor.floor_id}`);
      toast.success("Floor plan deleted");
      setActiveFloor(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  // Filter devices for pin picker by selected type
  const devicesByType = useMemo(() => {
    const already = new Set((activeFloor?.pins || []).map((p) => p.device_id));
    return devices.filter((d) => d.device_type === pinDeviceType && !already.has(d.device_id));
  }, [devices, pinDeviceType, activeFloor]);

  const toggleGroup = (g) => {
    const next = new Set(hiddenGroups);
    next.has(g) ? next.delete(g) : next.add(g);
    setHiddenGroups(next);
  };

  // Counts by group for legend chips
  const groupCounts = useMemo(() => {
    const counts = {};
    (activeFloor?.pins || []).forEach((p) => {
      const meta = equipmentMeta(p.device_type);
      counts[meta.group] = (counts[meta.group] || 0) + 1;
    });
    return counts;
  }, [activeFloor]);

  const isPdf = isPdfUrl(activeFloor?.image_url);
  const siteName = sites.find((s) => s.site_id === selectedSite)?.name;

  return (
    <div className="space-y-3" data-testid="floors-page">
      {/* Header — one compact row */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3 min-w-0 flex-wrap">
          <div className="w-9 h-9 rounded-lg bg-orange-50 border border-orange-200 text-orange-600 flex items-center justify-center shrink-0">
            <MapIcon className="w-5 h-5" strokeWidth={2} />
          </div>
          <h1 className="text-xl md:text-2xl font-extrabold tracking-tight">Floor Plans</h1>
          <span className="text-[11px] text-gray-500 truncate">
            Dashboard → {siteName} · live equipment pins colour-coded from telemetry
          </span>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {activeFloor && !isPdf && (
            <Button size="sm" onClick={exportPng} disabled={exporting} variant="outline" className="border-gray-300 text-gray-700 hover:bg-gray-50" data-testid="export-png-btn">
              <Download className="w-4 h-4 mr-1" strokeWidth={2.5} /> {exporting ? "Exporting…" : "Export PNG"}
            </Button>
          )}
          {canManage && activeFloor && (
            <Button size="sm" onClick={openEdit} variant="outline" className="border-gray-300 text-gray-700 hover:bg-gray-50" data-testid="edit-floor-btn">
              <Pencil className="w-4 h-4 mr-1" strokeWidth={2.5} /> Edit Floor
            </Button>
          )}
          {canManage && activeFloor && !isPdf && (
            <Button
              size="sm"
              onClick={() => setEditMode((e) => !e)}
              className={editMode
                ? "bg-orange-600 hover:bg-orange-700 text-white"
                : "bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"}
              data-testid="edit-mode-btn"
            >
              {editMode ? <><Eye className="w-4 h-4 mr-1" strokeWidth={2.5} /> Done Editing Pins</> : <><Edit3 className="w-4 h-4 mr-1" strokeWidth={2.5} /> Edit Pins</>}
            </Button>
          )}
          {canManage && (
          <Button size="sm" onClick={openAdd} className="f360-gradient border-0 text-white" data-testid="add-floor-btn">
            <Plus className="w-4 h-4 mr-1" strokeWidth={2.5} /> Add Floor Plan
          </Button>
          )}
        </div>
      </div>

      {/* Floor tabs */}
      {floors.length > 0 && (
        <div className="flex gap-1.5 overflow-x-auto pb-1">
          {floors.map((f) => {
            const active = activeFloor?.floor_id === f.floor_id;
            return (
              <button key={f.floor_id} onClick={() => setActiveFloor(f)}
                data-testid={`floor-tab-${f.floor_id}`}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap border transition ${active
                  ? "f360-gradient text-white border-transparent shadow-sm"
                  : "bg-white border-gray-200 text-gray-700 hover:border-orange-300 hover:bg-orange-50"}`}>
                {floorLabel(f)} — {f.name}
                <span className={`ml-2 px-1.5 rounded-full text-[10px] ${active ? "bg-white/25" : "bg-gray-100 text-gray-500"}`}>{f.pins?.length || 0}</span>
              </button>
            );
          })}
        </div>
      )}

      {/* Equipment legend / filter — with the edit-mode hint folded into the same strip */}
      {activeFloor && !isPdf && (
        <div className={`rounded-lg px-3 py-2 flex flex-wrap items-center gap-1.5 no-export border ${editMode ? "bg-orange-50 border-orange-200" : "glass border-transparent"}`} data-testid="equipment-legend">
          {editMode ? (
            <span className="text-[11px] text-orange-800 flex items-center gap-1.5 mr-2">
              <Edit3 className="w-3.5 h-3.5 text-orange-600" strokeWidth={2.5} />
              <b>Editing pins</b> — click the plan to add · drag a pin to move · click a pin to remove
            </span>
          ) : (
            <span className="text-[10px] uppercase tracking-widest text-gray-500 font-bold flex items-center gap-1 mr-1"><Filter className="w-3 h-3" strokeWidth={2.5} /> Show</span>
          )}
          {EQUIPMENT_GROUPS.map((g) => {
            const hidden = hiddenGroups.has(g.key);
            const count = groupCounts[g.key] || 0;
            return (
              <button
                key={g.key}
                onClick={() => toggleGroup(g.key)}
                data-testid={`legend-${g.key}`}
                className={`px-2 py-0.5 rounded-full text-[11px] font-semibold border transition ${hidden ? "bg-white border-gray-200 text-gray-400 line-through" : "text-gray-800"}`}
                style={hidden ? {} : { background: `${g.color}1a`, borderColor: `${g.color}66` }}
              >
                <span className="inline-block w-2 h-2 rounded-full mr-1.5 align-middle" style={{ background: hidden ? "#cbd5e1" : g.color }}></span>
                {g.label} <span className="opacity-60">· {count}</span>
              </button>
            );
          })}
          <div className="ml-auto flex items-center gap-3 text-[10px] text-gray-500">
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500"></span> OK</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-500"></span> Warn</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-500 animate-pulse"></span> Fault</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-slate-500"></span> Off</span>
          </div>
        </div>
      )}

      {activeFloor ? (
        isPdf ? (
          // Legacy PDF floor — pins can't be placed on a PDF; prompt to replace with an image.
          <div className="glass rounded-lg p-8 text-center">
            <FileWarning className="w-10 h-10 mx-auto text-amber-500 mb-3" strokeWidth={2} />
            <p className="text-gray-800 font-semibold">This floor plan was uploaded as a PDF.</p>
            <p className="text-sm text-gray-500 mt-1">Floor plans are images only now, so pins can be placed on them. Replace it with a PNG, JPEG or WebP export of the drawing.</p>
            {canManage && <div className="flex items-center justify-center gap-2 mt-4">
              <Button size="sm" onClick={openEdit} className="f360-gradient border-0 text-white"><Upload className="w-4 h-4 mr-1" strokeWidth={2.5} /> Replace with Image</Button>
              <Button size="sm" variant="ghost" onClick={deleteFloor} className="text-red-600 hover:text-red-700 hover:bg-red-50"><Trash2 className="w-4 h-4 mr-1" strokeWidth={2.5} /> Delete Floor</Button>
            </div>}
          </div>
        ) : (
          <div className="glass rounded-lg overflow-hidden" ref={canvasRef}>
            <div className="bg-slate-100" data-testid="floor-canvas">
              {/* Wrapper is exactly the image's box, so pin % coordinates map 1:1 onto the plan.
                  Its max width is derived from the plan's aspect ratio so the full height fits the
                  viewport without scrolling. */}
              <div
                className={`relative mx-auto ${editMode ? "cursor-crosshair" : "cursor-default"}`}
                onClick={handleImgClick}
                onMouseMove={onCanvasMouseMove}
                onMouseUp={onCanvasMouseUp}
                onMouseLeave={onCanvasMouseUp}
                style={{
                  touchAction: "pinch-zoom",
                  maxWidth: imgRatio ? `calc((100vh - 15rem) * ${imgRatio.toFixed(4)})` : undefined,
                }}
              >
                <img
                  ref={imgRef}
                  src={activeFloor.image_url}
                  alt={`${activeFloor.name} floor plan`}
                  className="block w-full h-auto select-none"
                  onLoad={onImgLoad}
                  draggable={false}
                />
                {(activeFloor.pins || []).map((p, i) => {
                  const meta = equipmentMeta(p.device_type);
                  if (hiddenGroups.has(meta.group)) return null;
                  const st = pinStatusFromTelemetry(p.device_type, tele, p.device_name);
                  const Icon = meta.icon;
                  const isDragging = draggingIdx === i;
                  return (
                    <button
                      key={i}
                      onClick={(e) => { e.stopPropagation(); if (!isDragging) setActivePin({ ...p, idx: i, meta, status: st }); }}
                      onMouseDown={(e) => onPinMouseDown(e, i)}
                      style={{ left: `${p.x}%`, top: `${p.y}%` }}
                      data-testid={`pin-${i}`}
                      title={`${meta.label} · ${p.device_name || ""} · ${st.label}${editMode ? " · drag to reposition" : ""}`}
                      className={`absolute -translate-x-1/2 -translate-y-1/2 w-7 h-7 rounded-full border-2 border-white ${STATUS_BG[st.code]} ring-4 ${STATUS_RING[st.code]} flex items-center justify-center shadow-lg transition ${editMode ? "cursor-grab active:cursor-grabbing" : "hover:scale-125"} ${isDragging ? "scale-125 z-20" : ""}`}
                    >
                      <Icon className="w-3.5 h-3.5 text-white pointer-events-none" />
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="px-3 py-2 flex items-center justify-between gap-2 text-xs border-t border-gray-200 no-export">
              <div className="text-gray-500 flex items-center gap-2 flex-wrap">
                <span><b className="text-gray-700">{activeFloor.pins?.length || 0}</b> pins · live polling 5s</span>
                <span className="text-gray-300">·</span>
                <span>{editMode ? <span className="flex items-center gap-1"><Move className="w-3 h-3" strokeWidth={2.5} /> Drag pins to reposition</span> : "Click a pin for details"}</span>
              </div>
              {canManage && <Button size="sm" variant="ghost" onClick={deleteFloor} className="text-red-600 hover:text-red-700 hover:bg-red-50 h-7" data-testid="delete-floor-btn"><Trash2 className="w-3.5 h-3.5 mr-1" strokeWidth={2.5} /> Delete Floor</Button>}
            </div>
          </div>
        )
      ) : (
        <div className="glass rounded-lg p-10 text-center">
          <ImagePlus className="w-12 h-12 mx-auto text-orange-500 mb-3" strokeWidth={2} />
          <p className="text-gray-800 font-semibold">No floor plans for this site yet</p>
          <p className="text-sm text-gray-500 mt-1">Upload a PNG, JPEG or WebP image of each floor, then place live equipment pins on it.</p>
          {canManage && <Button size="sm" onClick={openAdd} className="f360-gradient border-0 text-white mt-4"><Plus className="w-4 h-4 mr-1" strokeWidth={2.5} /> Add Floor Plan</Button>}
        </div>
      )}

      {/* Add / edit floor dialog */}
      <Dialog open={dialogOpen} onOpenChange={(o) => !saving && setDialogOpen(o)}>
        <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? `Edit ${editing.name || "Floor Plan"}` : "Add Floor Plan"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label>Floor Number</Label>
              <Input type="number" value={form.floor_number} onChange={(e) => setForm({ ...form, floor_number: Number(e.target.value) })} className="bg-gray-50 border-gray-200 mt-1" />
              <p className="text-[10px] text-gray-400 mt-1">0 = Basement</p>
            </div>
            <div className="col-span-2">
              <Label>Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-gray-50 border-gray-200 mt-1" placeholder="e.g. Ground Floor" />
            </div>
            <div className="col-span-3">
              <Label>Floor Plan Image</Label>
              <div className="mt-1">
                <ImageDropzone
                  value={form.image_url}
                  fileName={form.file_name}
                  onFile={handleFile}
                  onClear={() => setForm((f) => ({ ...f, image_url: "", file_name: "" }))}
                  testid="floor-dropzone"
                />
              </div>
              {editing && form.image_url && form.image_url !== editing.image_url && (editing.pins?.length || 0) > 0 && (
                <p className="text-[11px] text-amber-700 mt-1.5">
                  Existing {editing.pins.length} pins keep their positions — check them after replacing the image and drag any that no longer line up.
                </p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button>
            <Button onClick={saveFloor} disabled={saving} className="f360-gradient border-0 text-white" data-testid="save-floor-btn">
              {saving ? "Saving…" : editing ? "Save Changes" : "Add Floor"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Pin picker */}
      <Dialog open={showPinPicker} onOpenChange={setShowPinPicker}>
        <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-lg">
          <DialogHeader><DialogTitle>Place equipment pin</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Equipment type</Label>
              <Select value={pinDeviceType} onValueChange={(v) => { setPinDeviceType(v); setPinDevice(""); }}>
                <SelectTrigger className="bg-gray-50 border-gray-200 mt-1" data-testid="pin-type-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white border-gray-200 text-gray-900 max-h-72">
                  {Object.entries(EQUIPMENT).filter(([k]) => k !== "pump").map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Bind to specific device</Label>
              <Select value={pinDevice} onValueChange={setPinDevice}>
                <SelectTrigger className="bg-gray-50 border-gray-200 mt-1" data-testid="pin-device-select"><SelectValue placeholder={devicesByType.length ? "Select device" : "No available devices of this type"} /></SelectTrigger>
                <SelectContent className="bg-white border-gray-200 text-gray-900 max-h-72">
                  {devicesByType.map((d) => <SelectItem key={d.device_id} value={d.device_id}>{d.device_name}</SelectItem>)}
                </SelectContent>
              </Select>
              {!devicesByType.length && <div className="text-[11px] text-amber-700 mt-1">No free {EQUIPMENT[pinDeviceType]?.label}s left. Add more devices in Device Management.</div>}
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowPinPicker(false)}>Cancel</Button>
            <Button onClick={savePin} disabled={!pinDevice} className="f360-gradient border-0 text-white" data-testid="save-pin-btn">Place Pin</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Pin detail drawer */}
      <Sheet open={!!activePin} onOpenChange={(o) => !o && setActivePin(null)}>
        <SheetContent side="right" className="bg-white border-gray-200 text-gray-900 w-full sm:max-w-md">
          {activePin && (
            <>
              <SheetHeader className="border-b border-gray-200 pb-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className="inline-flex items-center justify-center w-9 h-9 rounded-lg" style={{ background: activePin.meta.color + "22" }}>
                    <activePin.meta.icon className="w-5 h-5" style={{ color: activePin.meta.color }} />
                  </span>
                  <Badge style={{ background: activePin.meta.color + "1a", color: activePin.meta.color }} className="border-0 text-[10px]">{activePin.meta.label}</Badge>
                </div>
                <SheetTitle className="text-gray-900 text-xl">{activePin.device_name}</SheetTitle>
                <div className="text-xs text-gray-500">Device ID: <span className="font-mono text-orange-600">{activePin.device_id}</span></div>
              </SheetHeader>
              <div className="py-4 space-y-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="text-[10px] uppercase tracking-widest text-gray-500 font-bold mb-2">Live Status</div>
                  <div className="flex items-center gap-3">
                    <span className={`w-3 h-3 rounded-full ${STATUS_BG[activePin.status.code]} ${activePin.status.code === "ok" ? "status-pulse-green" : activePin.status.code === "fault" ? "status-pulse-red" : ""}`}></span>
                    <div>
                      <div className="text-lg font-extrabold uppercase">{activePin.status.label}</div>
                      {activePin.status.value && <div className="text-xs text-gray-500 font-mono">{activePin.status.value}</div>}
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div className="bg-gray-50 rounded p-3"><div className="text-gray-500">Type</div><div className="font-bold mt-0.5">{activePin.device_type}</div></div>
                  <div className="bg-gray-50 rounded p-3"><div className="text-gray-500">Last sync</div><div className="font-bold mt-0.5">{tele?.updated_at ? new Date(tele.updated_at).toLocaleTimeString("en-IN") : "—"}</div></div>
                </div>
                <a href={`/devices/${activePin.device_id}/history`} className="block text-center f360-gradient text-white rounded-lg py-2.5 font-semibold">View Device History →</a>
                {canManage && editMode && (
                  <Button onClick={() => deletePin(activePin.idx)} className="w-full bg-red-600 hover:bg-red-700 text-white">
                    <Trash2 className="w-4 h-4 mr-1" strokeWidth={2.5} /> Remove Pin
                  </Button>
                )}
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
